import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Loader2, AlertTriangle, CheckCircle, ShieldAlert, ShieldCheck,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { MatchEvidence, ScreeningTracePanel } from '@/components/MatchEvidence';
import { groupMatchesByEntity } from '@/lib/groupMatches';
import {
  matchedNameType, nameTypeClass, listingIdentity, groupNameClassifications,
} from '@/lib/nameTypes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

const getRiskStyle = (level) => {
  switch (level) {
    case 'critical': return { bg: 'bg-rose-50 border-rose-300', text: 'text-rose-700', badge: 'bg-rose-600 text-white', icon: ShieldAlert };
    case 'high': return { bg: 'bg-rose-50 border-rose-200', text: 'text-rose-600', badge: 'bg-rose-500 text-white', icon: AlertTriangle };
    case 'medium': return { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', badge: 'bg-amber-500 text-white', icon: AlertTriangle };
    case 'low': return { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', badge: 'bg-blue-500 text-white', icon: ShieldCheck };
    default: return { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-500 text-white', icon: CheckCircle };
  }
};

export default function DeepScanPage({ user, onLogout }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('validating'); // validating | scanning | done | error
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [expandedMatch, setExpandedMatch] = useState(null);
  // Which listing of a grouped card the reviewer is reading, per card. The
  // details shown always belong to the listing that was opened.
  const [activeListing, setActiveListing] = useState({});
  const hasRun = useRef(false);

  // One card per entity, naming every list it appears on.
  const matchGroups = useMemo(
    () => groupMatchesByEntity(result?.matches || []),
    [result]
  );

  const rawName = searchParams.get('name');
  const idno = searchParams.get('idno');
  const rawDob = searchParams.get('dob');
  const nationality = searchParams.get('nationality');
  const gender = searchParams.get('gender');
  const rawSearchType = searchParams.get('searchType') || 'individual';

  // Auto-detect business by company suffixes in the name.
  // Vendors (e.g., MoneyPro) use a single form for both individuals and companies,
  // so the URL may arrive as searchType=individual even for corporate customers.
  // We override to "business" when the name clearly indicates a company.
  const detectBusiness = (input) => {
    if (!input) return false;
    const v = decodeURIComponent(input).toLowerCase();
    // Malaysian, Singaporean, GCC, and international company suffix patterns
    const patterns = [
      /\bsdn\.?\s*bhd\.?\b/, /\bberhad\b/, /\bsendirian\b/,
      /\bpte\.?\s*ltd\.?\b/, /\bpvt\.?\s*ltd\.?\b/,
      /\bltd\.?\b/, /\bllc\b/, /\binc\.?\b/, /\bcorp\.?\b/, /\bcorporation\b/,
      /\bcompany\b/, /\bco\.\b/, /\bgmbh\b/, /\bs\.?a\.?\b/, /\bplc\b/,
      /\bholdings?\b/, /\bgroup\b/, /\benterprise(s)?\b/, /\btrading\b/,
      /\bventures?\b/, /\bcapital\b/, /\bpartners?\b/, /\bassociates?\b/,
      /\bsolutions?\b/, /\bservices?\b/, /\btechnologies?\b/, /\bsystems?\b/,
      /\bindustries\b/, /\bmanufacturing\b/, /\binvestments?\b/,
      /\bagency\b/, /\bbureau\b/, /\bfoundation\b/, /\binstitute\b/,
      /\bbank\b/, /\bexchange\b/, /\bremittance\b/,
      /\bsons?\b/, /&\s*co\b/, /\bn\.?v\.?\b/, /\bag\b/, /\bb\.?v\.?\b/,
    ];
    return patterns.some(re => re.test(v));
  };

  const autoDetectedBusiness = rawSearchType === 'individual' && detectBusiness(rawName);
  const searchType = autoDetectedBusiness ? 'business' : rawSearchType;
  const name = rawName;

  // Normalize DOB to YYYY-MM-DD. Accepts DD/MM/YYYY, DD-MM-YYYY, YYYY-MM-DD, YYYY/MM/DD.
  const normalizeDob = (input) => {
    if (!input) return null;
    const v = decodeURIComponent(input).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    if (/^\d{4}\/\d{2}\/\d{2}$/.test(v)) return v.replace(/\//g, '-');
    const m = v.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (m) {
      const [, d, mo, y] = m;
      return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    return v; // fallback — let backend reject if truly invalid
  };
  const dob = normalizeDob(rawDob);

  const runScan = useCallback(async () => {
    setStatus('scanning');
    try {
      const isBusiness = searchType === 'business';
      const payload = {
        name: decodeURIComponent(name),
        entity_type: isBusiness ? 'organization' : 'individual',
      };
      // For business scans, skip DOB/nationality/gender (vendor's DOB is just a placeholder for 18+ check)
      if (!isBusiness && dob) payload.date_of_birth = dob;
      if (!isBusiness && nationality) payload.nationality = nationality;
      if (!isBusiness && gender) payload.gender = gender;
      if (idno) payload.additional_info = { id_number: decodeURIComponent(idno) };

      const response = await axios.post(`${API}/scan`, payload, { headers: getAuthHeaders() });
      setResult(response.data);
      setStatus('done');
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.detail || 'Screening failed. Please try again.');
      toast.error('Screening failed');
    }
  }, [name, searchType, idno, dob, nationality, gender]);

  // Deep links are scanned exactly once per mount — the hasRun guard means a new
  // runScan identity (when a query param changes) cannot re-fire the scan.
  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    // Validate required params
    if (!name) {
      setStatus('error');
      setError('Missing required parameter: name');
      return;
    }

    if (searchType === 'individual' && (!idno || !dob)) {
      setStatus('error');
      setError(`Missing required parameters for Individual screening: ${!idno ? 'idno' : ''} ${!dob ? 'dob' : ''}`.trim());
      return;
    }

    if (searchType === 'business' && !idno) {
      setStatus('error');
      setError('Missing required parameter: idno');
      return;
    }

    runScan();
  }, [name, searchType, idno, dob, runScan]);

  const riskStyle = result ? getRiskStyle(result.risk_level) : null;
  const RiskIcon = riskStyle?.icon || CheckCircle;

  return (
    <Layout user={user} onLogout={onLogout} activePage="scan">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Header with query info */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="deep-scan-title">
              {searchType === 'business' ? 'Business' : 'Individual'} Screening
            </h1>
            <p className="text-sm text-slate-500 mt-1">Deep link scan via MoneyPro</p>
            {autoDetectedBusiness && (
              <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-50 border border-indigo-200" data-testid="auto-detect-business-badge">
                <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
                <span className="text-[11px] font-medium text-indigo-700">Auto-detected as business entity — scanned against organization watchlists</span>
              </div>
            )}
          </div>
          {result && (
            <Badge className={`text-sm px-3 py-1 ${riskStyle.badge}`} data-testid="deep-scan-risk-badge">
              {result.risk_level.toUpperCase()}
            </Badge>
          )}
        </div>

        {/* Query Parameters Card */}
        <Card className="border border-slate-200 p-5" data-testid="deep-scan-params">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Screening Parameters</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Name</p>
              <p className="text-sm font-semibold text-slate-900 mt-0.5" data-testid="param-name">{name ? decodeURIComponent(name) : '-'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">ID Number</p>
              <p className="text-sm font-mono text-slate-900 mt-0.5" data-testid="param-idno">{idno ? decodeURIComponent(idno) : '-'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Date of Birth</p>
              <p className="text-sm text-slate-900 mt-0.5" data-testid="param-dob">{dob || '-'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Nationality</p>
              <p className="text-sm text-slate-900 mt-0.5" data-testid="param-nationality">{nationality || '-'}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">Gender</p>
              <p className="text-sm text-slate-900 mt-0.5 capitalize" data-testid="param-gender">{gender || '-'}</p>
            </div>
          </div>
        </Card>

        {/* Loading State */}
        {status === 'validating' && (
          <Card className="border border-slate-200 p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
            <p className="text-sm text-slate-500 mt-3">Validating parameters...</p>
          </Card>
        )}

        {status === 'scanning' && (
          <Card className="border border-blue-200 bg-blue-50 p-12 text-center" data-testid="deep-scan-loading">
            <Loader2 className="w-10 h-10 animate-spin text-blue-500 mx-auto" />
            <p className="text-base font-medium text-blue-800 mt-4">Screening in progress...</p>
            <p className="text-sm text-blue-600 mt-1">Checking against OFAC, PEP, MOHA, UNSCR watchlists</p>
          </Card>
        )}

        {/* Error State */}
        {status === 'error' && (
          <Card className="border border-rose-200 bg-rose-50 p-8 text-center" data-testid="deep-scan-error">
            <AlertTriangle className="w-10 h-10 text-rose-500 mx-auto" />
            <p className="text-base font-semibold text-rose-800 mt-3">Screening Error</p>
            <p className="text-sm text-rose-600 mt-1">{error}</p>
            <Button
              onClick={() => navigate('/scan')}
              variant="outline"
              className="mt-4 border-rose-300 text-rose-700 hover:bg-rose-100"
            >
              Go to Manual Scan
            </Button>
          </Card>
        )}

        {/* Results */}
        {status === 'done' && result && (
          <>
            {result.screening_trace && <ScreeningTracePanel trace={result.screening_trace} />}
            {/* Risk Summary */}
            <Card className={`border-2 ${riskStyle.bg} p-6`} data-testid="deep-scan-risk-summary">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center ${riskStyle.badge}`}>
                  <RiskIcon className="w-7 h-7" />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${riskStyle.text}`}>
                    {result.risk_level === 'clear' ? 'No Matches Found' : `${matchGroups.length} Match${matchGroups.length !== 1 ? 'es' : ''} Found`}
                  </p>
                  <p className="text-sm text-slate-600 mt-0.5">
                    Risk Level: <strong className="uppercase">{result.risk_level}</strong>
                    {result.matches.length > matchGroups.length && ` | ${result.matches.length} listings`}
                    {result.identity_verification_enabled && ' | Multi-factor identity verification applied'}
                  </p>
                </div>
              </div>
            </Card>

            {/* Matches List */}
            {matchGroups.length > 0 && (
              <div className="space-y-3" data-testid="deep-scan-matches">
                {matchGroups.map(({ key, primary, sources, listings }, idx) => {
                  // The card header summarises the strongest listing; the body
                  // shows one exact record, chosen by the reviewer.
                  const activeIdx = Math.min(activeListing[key] ?? 0, listings.length - 1);
                  const match = listings[activeIdx] || primary;
                  const identity = listingIdentity(match);
                  const mStyle = getRiskStyle(
                    match.identity_verification?.risk_level ||
                    (match.score >= 90 ? 'high' : match.score >= 75 ? 'medium' : 'low')
                  );
                  const isExpanded = expandedMatch === idx;
                  const iv = match.identity_verification;

                  return (
                    <Card key={key} className={`border ${mStyle.bg} overflow-hidden`} data-testid={`deep-match-${idx}`}>
                      <div
                        className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/30 transition-colors"
                        onClick={() => setExpandedMatch(isExpanded ? null : idx)}
                      >
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className="text-center shrink-0">
                            <p className={`text-2xl font-bold ${mStyle.text}`}>{match.score?.toFixed(0)}%</p>
                            <p className="text-[10px] text-slate-500">confidence</p>
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 truncate">{match.matched_name}</p>
                            <p
                              className="text-[10px] text-slate-500 font-mono mt-0.5"
                              data-testid={`deep-match-record-identity-${idx}`}
                            >
                              {identity.authority || 'source not recorded'} · listing{' '}
                              {identity.recordId || 'ID not recorded'}
                            </p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <Badge
                                variant="outline"
                                className={`text-[10px] border ${nameTypeClass(matchedNameType(match))}`}
                                data-testid={`deep-matched-name-type-${idx}`}
                              >
                                {matchedNameType(match)}
                              </Badge>
                              {sources.map((source) => (
                                <Badge
                                  key={`src-${key}-${source}`}
                                  variant="outline"
                                  className="text-[10px]"
                                  data-testid={`deep-match-source-${idx}-${source.replace(/\s+/g, '-').toLowerCase()}`}
                                >
                                  {source}
                                </Badge>
                              ))}
                              {listings.length > 1 && (
                                <span className="text-[10px] text-slate-500">{listings.length} listings</span>
                              )}
                              <Badge variant="outline" className="text-[10px]">{match.match_type}</Badge>
                              {iv?.verdict && (
                                <Badge className={`text-[10px] ${
                                  iv.verdict === 'POSITIVE_MATCH' ? 'bg-rose-600 text-white' :
                                  iv.verdict === 'PROBABLE_MATCH' ? 'bg-rose-500 text-white' :
                                  iv.verdict === 'SANCTIONS_LIST_HIT' ? 'bg-rose-600 text-white' :
                                  iv.verdict === 'POSSIBLE_MATCH' ? 'bg-amber-500 text-white' :
                                  'bg-blue-500 text-white'
                                }`}>
                                  {iv.verdict === 'SANCTIONS_LIST_HIT' ? 'HIGH — SANCTIONS LISTING' : iv.verdict.replace(/_/g, ' ')}
                                </Badge>
                              )}
                              {/* UNSCR program tags */}
                              {match.details?.program_ids?.map((pid) => (
                                <Badge key={`prog-${pid}`} className="bg-orange-100 text-orange-800 border border-orange-200 text-[10px] font-mono">{pid}</Badge>
                              ))}
                              {match.details?.programs?.map((p) => (
                                <Badge key={`p-${p}`} className="bg-rose-100 text-rose-800 text-[10px]">{p}</Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="shrink-0 ml-2 flex items-center gap-2" data-testid={`deep-match-toggle-${idx}`}>
                          <span className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold hidden sm:inline">
                            {isExpanded ? 'Hide details' : 'View details'}
                          </span>
                          {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-600" /> : <ChevronDown className="w-5 h-5 text-slate-600" />}
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-slate-200 bg-white p-4 space-y-3">
                          {listings.length > 1 && (
                            <div
                              className="border border-slate-200 rounded divide-y divide-slate-200"
                              data-testid={`deep-match-listings-${idx}`}
                            >
                              <p className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-50">
                                Listings for this party — the details below are the one selected
                              </p>
                              {listings.map((listing, lidx) => {
                                const ident = listingIdentity(listing);
                                const isActive = lidx === activeIdx;
                                return (
                                  <div
                                    key={`deep-listing-${key}-${ident.authority}-${ident.recordId}-${lidx}`}
                                    className={`px-3 py-2 flex items-center justify-between gap-3 ${isActive ? 'bg-slate-50' : ''}`}
                                    data-testid={`deep-match-listing-${idx}-${lidx}`}
                                  >
                                    <div className="min-w-0">
                                      <p className="text-sm text-slate-800 truncate">{listing.matched_name}</p>
                                      <p className="text-xs text-slate-500 font-mono">
                                        {ident.authority || 'source not recorded'} · listing{' '}
                                        {ident.recordId || 'ID not recorded'}
                                      </p>
                                    </div>
                                    {isActive ? (
                                      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 shrink-0">
                                        Showing
                                      </span>
                                    ) : (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="shrink-0"
                                        onClick={() => setActiveListing((prev) => ({ ...prev, [key]: lidx }))}
                                        data-testid={`deep-open-listing-${idx}-${lidx}`}
                                      >
                                        View record
                                      </Button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          <MatchEvidence match={match} index={idx} />
                          {/* Details grid */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            {match.details?.birth_date && (
                              <div>
                                <p className="text-[10px] uppercase text-slate-400">DOB</p>
                                <p className="text-slate-700">{match.details.birth_date}</p>
                              </div>
                            )}
                            {(match.details?.nationality?.length > 0 || match.details?.citizenship?.length > 0) && (
                              <div>
                                <p className="text-[10px] uppercase text-slate-400">Nationality</p>
                                <p className="text-slate-700">{(match.details.nationality || match.details.citizenship || []).join(', ')}</p>
                              </div>
                            )}
                            {match.details?.gender && (
                              <div>
                                <p className="text-[10px] uppercase text-slate-400">Gender</p>
                                <p className="text-slate-700 capitalize">{Array.isArray(match.details.gender) ? match.details.gender[0] : match.details.gender}</p>
                              </div>
                            )}
                            {groupNameClassifications(match.details?.name_classifications || [])
                              .filter((g) => g.type !== 'Primary name')
                              .map(({ type, names }) => (
                                <div className="col-span-2" key={`deep-names-${idx}-${type}`}
                                  data-testid={`deep-name-group-${idx}-${type.replace(/\s+/g, '-').toLowerCase()}`}>
                                  <p className="text-[10px] uppercase text-slate-400">{type}</p>
                                  <p className="text-slate-700 text-xs">
                                    {names.slice(0, 5).join(', ')}
                                    {names.length > 5 && ` +${names.length - 5} more`}
                                  </p>
                                </div>
                              ))}
                          </div>

                          {/* Identity Verification */}
                          {iv && (
                            <div className="bg-slate-50 rounded p-3">
                              <p className="text-xs font-semibold text-slate-700 mb-2">Identity Verification</p>
                              <div className="grid grid-cols-3 gap-2 text-xs">
                                {iv.match_factors?.dob && (
                                  <div className={`p-2 rounded border ${iv.match_factors.dob.result === 'match' ? 'border-emerald-200 bg-emerald-50' : iv.match_factors.dob.result === 'mismatch' ? 'border-rose-200 bg-rose-50' : 'border-slate-200'}`}>
                                    <p className="text-[10px] text-slate-500">Date of Birth</p>
                                    <p className="font-medium capitalize">{iv.match_factors.dob.result}</p>
                                  </div>
                                )}
                                {iv.match_factors?.nationality && (
                                  <div className={`p-2 rounded border ${iv.match_factors.nationality.result === 'match' ? 'border-emerald-200 bg-emerald-50' : iv.match_factors.nationality.result === 'mismatch' ? 'border-rose-200 bg-rose-50' : 'border-slate-200'}`}>
                                    <p className="text-[10px] text-slate-500">Nationality</p>
                                    <p className="font-medium capitalize">{iv.match_factors.nationality.result}</p>
                                  </div>
                                )}
                                {iv.match_factors?.gender && (
                                  <div className={`p-2 rounded border ${iv.match_factors.gender.result === 'match' ? 'border-emerald-200 bg-emerald-50' : iv.match_factors.gender.result === 'mismatch' ? 'border-rose-200 bg-rose-50' : 'border-slate-200'}`}>
                                    <p className="text-[10px] text-slate-500">Gender</p>
                                    <p className="font-medium capitalize">{iv.match_factors.gender.result}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* UNSCR Sanctions Details */}
                          {match.details?.sanctions_details?.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-slate-700 mb-1">UN Sanctions Programs</p>
                              <div className="overflow-x-auto border border-slate-200 rounded">
                                <table className="w-full text-xs">
                                  <thead className="bg-orange-50">
                                    <tr>
                                      <th className="text-left px-2 py-1.5 text-orange-800">Program</th>
                                      <th className="text-left px-2 py-1.5 text-orange-800">Program ID</th>
                                      <th className="text-left px-2 py-1.5 text-orange-800">Resolution</th>
                                      <th className="text-left px-2 py-1.5 text-orange-800">Reference</th>
                                      <th className="text-left px-2 py-1.5 text-orange-800">First listed</th>
                                      <th className="text-left px-2 py-1.5 text-orange-800">Last amended</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {match.details.sanctions_details.map((sd, si) => (
                                      <tr key={`sd-${sd.program_id || sd.program || si}-${si}`} className="border-t border-slate-100">
                                        <td className="px-2 py-1.5">{sd.program || '-'}</td>
                                        <td className="px-2 py-1.5 font-mono">{sd.program_id || '-'}</td>
                                        <td className="px-2 py-1.5 font-mono">{sd.un_resolution || '-'}</td>
                                        <td className="px-2 py-1.5 font-mono">{sd.unsc_id || '-'}</td>
                                        <td className="px-2 py-1.5">{sd.listing_date || sd.start_date || '-'}</td>
                                        <td className="px-2 py-1.5">{sd.modified_date || match.details.modified_at || '-'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3 pt-2">
              <Button
                onClick={() => navigate('/results', { state: { scanId: result.id } })}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="deep-scan-view-full"
              >
                View Full Report
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  // window.close() only works for windows opened via window.open().
                  // When MoneyPro opens our URL via target="_blank" / new tab, the browser
                  // refuses to close it. We attempt close, and if it fails, show a clear
                  // instruction to the user.
                  try {
                    window.close();
                    // Give the browser ~250ms to actually close; if we're still here, fall back.
                    setTimeout(() => {
                      if (!window.closed) {
                        toast.info('This tab cannot be closed automatically. Please close it manually (Ctrl+W) to return to MoneyPro.', {
                          duration: 8000,
                        });
                      }
                    }, 300);
                  } catch (e) {
                    toast.info('Please close this tab manually (Ctrl+W) to return to MoneyPro.', { duration: 8000 });
                  }
                }}
                data-testid="deep-scan-close"
              >
                Close & Return to MoneyPro
              </Button>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
